import type { RequestOptions } from '@nuxtjs/algolia'

interface ServiceProviderFilterParams {
  'service-type'?: string
  audiences?: string
  states?: string
}

interface ResourceFilterParams {
  'resource-type'?: string
  topics?: string
  audiences?: string
  states?: string
}

export default function useFiltersWithIndices({
  contentType,
  componentType,
}: { contentType?: Ref<string>; componentType?: Ref<string> } = {}) {
  const DEFAULT_FILTERS: RequestOptions = {
    typoTolerance: true,
    removeWordsIfNoResults: 'allOptional',
    queryType: 'prefixAll',
    naturalLanguages: ['en'],
    sumOrFiltersScores: true,
  }

  const getAudienceFilters = (audiences) => {
    const audienceFilters = []
    const optionalAudienceFilters = []

    if (audiences?.length) {
      audiences.split(',').forEach((audience) => {
        audienceFilters.push(`audiences:${audience}<score=2>`)
      })
      // Note: this always includes the 'all' audience, which makes filtering less precise (but more inclusive)
      if (!audiences.includes('all')) {
        audienceFilters.push('audiences:all<score=1>')
      }

      optionalAudienceFilters.push(['audiences:-all'])
    }

    return {
      audienceFilters,
      optionalAudienceFilters,
    }
  }

  const getStateFilters = (states) => {
    const stateFilters = []
    const optionalStateFilters = []

    if (states?.length) {
      const statesList = states.split(',')
      // Note: this always includes the 'national' state, which makes filtering less precise (but more inclusive)
      if (!statesList.includes('national')) {
        stateFilters.push('states:national<score=1>')
      }
      statesList.forEach((state) => {
        stateFilters.push(`states:${state}<score=2>`)
      })

      optionalStateFilters.push(['states:-national'])
    }

    return {
      stateFilters,
      optionalStateFilters,
    }
  }

  const mapSearchFilters = (attribute, filters) =>
    filters
      ?.split(',')
      ?.map((serviceType) => `${attribute}:${serviceType}<score=2>`) || []

  const serviceProviderFilterFormatter = (
    params: ServiceProviderFilterParams = {},
  ) => {
    const facetFilters: (string | string[])[] = ['component:ServiceProviders']
    const optionalFilters = []

    const typeFilters = mapSearchFilters(
      'serviceType',
      params?.['service-type'],
    )
    if (typeFilters.length) facetFilters.push(typeFilters)

    const { audienceFilters, optionalAudienceFilters } = getAudienceFilters(
      params?.audiences,
    )
    if (audienceFilters.length) facetFilters.push(audienceFilters)
    if (optionalAudienceFilters.length)
      optionalFilters.push(optionalAudienceFilters)

    const { stateFilters, optionalStateFilters } = getStateFilters(
      params?.states,
    )
    if (stateFilters.length) facetFilters.push(stateFilters)
    if (optionalStateFilters.length) optionalFilters.push(optionalStateFilters)

    return {
      facetFilters,
      optionalFilters,
      ...DEFAULT_FILTERS,
    }
  }

  const resourcesFilterFormatter = (params: ResourceFilterParams = {}) => {
    const facetFilters: (string | string[])[] = ['component:Resource']
    const optionalFilters = []

    const typeFilters = mapSearchFilters('type', params?.['resource-type'])
    if (typeFilters.length) facetFilters.push(typeFilters)

    const topicFilters = mapSearchFilters('topics', params?.topics)
    if (topicFilters.length) facetFilters.push(topicFilters)

    const { audienceFilters, optionalAudienceFilters } = getAudienceFilters(
      params?.audiences,
    )
    if (audienceFilters.length) facetFilters.push(audienceFilters)
    if (optionalAudienceFilters.length)
      optionalFilters.push(optionalAudienceFilters)

    const { stateFilters, optionalStateFilters } = getStateFilters(
      params?.states,
    )
    if (stateFilters.length) facetFilters.push(stateFilters)
    if (optionalStateFilters.length) optionalFilters.push(optionalStateFilters)

    return {
      facetFilters,
      optionalFilters,
      ...DEFAULT_FILTERS,
    }
  }

  const searchFilterFormatterForComponent = (type) => {
    if (type === 'ServiceProviders') return serviceProviderFilterFormatter
    return resourcesFilterFormatter
  }

  const searchFilterFormatter = searchFilterFormatterForComponent(
    componentType?.value,
  )

  const route = useRoute()
  const searchFilters = computed(() => searchFilterFormatter(route.query))
  const filterData = useFilters({ contentType, componentType })

  return {
    searchFilters,
    ...filterData,
  }
}
